import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { get } from 'lodash';
import { Formik } from 'formik';
import jstz from 'jstz';
import { useMutation } from '@apollo/client';
import CCEvents, { EventNames } from '@companycam/companycam-events';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  LD_SIGN_UP_REQUIRE_HOW_HEARD_DETAIL_FLAG,
  LD_SIGN_UP_REQUIRE_COMPANY_SIZE_FLAG,
} from 'constants/featureFlags';
import isMobile from 'helpers/isMobile';
import toast from 'helpers/toast';

import { ArrowLeft } from '../shared/DesignSystem/Icons';
import EmailPasswordCompanyName from './steps/EmailPasswordCompanyName';
import MyProfile from './steps/MyProfile';
import ACCOUNT_CREATE_MUTATION from './gql/accountCreateMutation.graphql';
import { EMAIL_PASSWORD, MY_PROFILE } from './constants';
import getCreateAccountValidation from './getCreateAccountValidation';
import { translate } from 'javascripts/translations/i18n';

// #region styled-components
const BackDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  margin-bottom: 32px;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  margin-left: -6px;
`;

const ContentDiv = styled.div`
  padding: 16px;
  width: 500px;
  @media (max-width: 769px) {
    width: 100vw;
  }
`;

const BackText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #a9adb1;
  text-transform: capitalize;
`;
// #endregion

const errorsFromResultData = (data) => get(data, 'accountCreate.errors', []);
const tokenFromResultData = (data) => get(data, 'accountCreate.token', null);

const SignupForm = ({
  currentStep,
  previousStepName,
  currentStepName,
  gclid,
  stepForward,
  stepBackward,
  emailFromParams,
  utmSourceFromParams,
  utmMediumFromParams,
  utmCampaignFromParams,
  rsCodeFromParams,
  projectInviteToken,
}) => {
  const backButton = currentStep > 0 && (
    <BackDiv onClick={stepBackward}>
      <StyledArrowLeft size={18} color="#A9ADB1" />
      <BackText>
        {translate('onboard.backButton.text', { prev: previousStepName })}
      </BackText>
    </BackDiv>
  );
  const flags = useFlags();
  const requireHowHeardDetail = flags[LD_SIGN_UP_REQUIRE_HOW_HEARD_DETAIL_FLAG];
  const requireCompanySize = flags[LD_SIGN_UP_REQUIRE_COMPANY_SIZE_FLAG];

  const logRegistrationStart = () =>
    CCEvents.logEvent(EventNames.START_REGISTRATION);

  useEffect(logRegistrationStart, []);

  const initialValues = {
    email: emailFromParams || '',
    password: '',
    firstName: '',
    lastName: '',
    companyName: '',
    phoneNumber: '',
    industry: '',
    howHeardType: '',
    howHeardDetail: '',
    companySize: '',
    primaryUserTitle: '',
    device: 'web_v2',
  };

  // Bypass the authentication screen with a hidden login
  const loginNewUser = (email, password) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
    const form = document.createElement('form');
    form.setAttribute('method', 'post');
    form.setAttribute('action', '/users/sign_in');

    const hiddenEmailField = document.createElement('input');
    hiddenEmailField.setAttribute('type', 'hidden');
    hiddenEmailField.setAttribute('name', 'user[email_address]');
    hiddenEmailField.setAttribute('value', email);

    const hiddenPasswordField = document.createElement('input');
    hiddenPasswordField.setAttribute('type', 'hidden');
    hiddenPasswordField.setAttribute('name', 'user[password]');
    hiddenPasswordField.setAttribute('value', password);

    const hiddenTokenField = document.createElement('input');
    hiddenTokenField.setAttribute('type', 'hidden');
    hiddenTokenField.setAttribute('name', 'authenticity_token');
    hiddenTokenField.setAttribute('value', csrfToken);

    form.appendChild(hiddenEmailField);
    form.appendChild(hiddenPasswordField);
    form.appendChild(hiddenTokenField);

    document.body.appendChild(form);
    form.submit();
    // Upon successful login, Devise redirects us to the location that was
    // stored by App::SignupController#new.
  };

  const [createAccountMutation, { loading: mutationInFlight }] = useMutation(
    ACCOUNT_CREATE_MUTATION,
    {
      onCompleted: async (data) => {
        const gqlErrors = errorsFromResultData(data);

        if (errorsFromResultData(data).length) {
          setTimeout(() => {
            toast.error(gqlErrors[0].message);
          }, 250);
        }
      },
      onError: (mutationError) => {
        console.error('account_create_mutation_error', {
          mutationError,
        });
      },
    },
  );

  // Grab all cc-* from cookie to be passed into the js form
  const parsedCookieValues = document.cookie
    .split(';')
    .filter((x) => x.trim().startsWith('cc-'))
    .map((v) => v.split('='))
    .reduce((acc, v) => {
      acc[decodeURIComponent(v[0].trim().replace('cc-', ''))] =
        decodeURIComponent(v[1].trim());
      return acc;
    }, {});

  const createAccount = async (values) => {
    const newAccountValues = {
      variables: {
        input: {
          company: {
            name: values.companyName,
            timeZone: jstz.determine().name(),
            industry: {
              id: values.industry,
            },
            howHeard: {
              type: values.howHeardType,
              detail: values.howHeardDetail,
            },
          },
          user: {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phoneNumber: values.phoneNumber,
            password: values.password,
            acceptedTos: values.acceptedTos,
          },
          deviceName: values.device,
          marketing: {
            utmSource: parsedCookieValues.utm_source || utmSourceFromParams,
            utmMedium:
              parsedCookieValues.utm_medium ||
              rsCodeFromParams ||
              utmMediumFromParams,
            utmCampaign:
              parsedCookieValues.utm_campaign || utmCampaignFromParams,
            utmTerm: parsedCookieValues.utm_term,
            utmContent: parsedCookieValues.utm_content,
            referral: parsedCookieValues.referral,
            gclid: parsedCookieValues.gclid || gclid,
            fbclid: parsedCookieValues.fbclid,
            primaryUserTitle: values.primaryUserTitle,
          },
        },
      },
    };

    if (requireCompanySize) {
      newAccountValues.variables.input.company.companySize = values.companySize;
    }

    const result = await createAccountMutation(newAccountValues);

    if (tokenFromResultData(result.data)) {
      loginNewUser(values.email, values.password);
    }
  };

  const formValidationSchema = getCreateAccountValidation({
    isMobile,
    requireCompanySize,
    requireHowHeardDetail,
  });

  return (
    <ContentDiv>
      {backButton}
      <Formik
        initialValues={initialValues}
        validationSchema={formValidationSchema}
        onSubmit={createAccount}
      >
        {({ errors, handleSubmit }) => (
          <form>
            <EmailPasswordCompanyName
              errors={errors}
              currentStepName={currentStepName}
              stepName={EMAIL_PASSWORD}
              buttonAction={stepForward}
              projectInviteToken={projectInviteToken}
            />
            <MyProfile
              previousStepName={previousStepName}
              currentStepName={currentStepName}
              stepName={MY_PROFILE}
              buttonAction={handleSubmit}
              projectInviteToken={projectInviteToken}
              disabled={mutationInFlight}
              requireCompanySize={requireCompanySize}
            />
          </form>
        )}
      </Formik>
    </ContentDiv>
  );
};

SignupForm.propTypes = {
  currentStep: PropTypes.number,
  previousStepName: PropTypes.string,
  currentStepName: PropTypes.string,
  gclid: PropTypes.string,
  stepForward: PropTypes.func,
  stepBackward: PropTypes.func,
  emailFromParams: PropTypes.string,
  utmSourceFromParams: PropTypes.string,
  utmMediumFromParams: PropTypes.string,
  utmCampaignFromParams: PropTypes.string,
  rsCodeFromParams: PropTypes.string,
  projectInviteToken: PropTypes.string,
};

export default SignupForm;
